import { useId } from 'react';
import { Button } from '@zep-us/zus';
import { LoadingSpinner } from '@/shared/components';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { canSubmitAiBuilderQuestionSet, getActiveQuestionAnswer } from '../store/aiBuilderQuestionSetState';
import { type AiBuilderPendingQuestionSet } from '../store/types';
import { AiBuilderQuestionAnswerControls } from './AiBuilderQuestionAnswerControls';
import { AiBuilderQuestionSetHeader } from './AiBuilderQuestionSetHeader';
export type AiBuilderQuestionSetComposerProps = {
  questionSet: AiBuilderPendingQuestionSet;
  submitting?: boolean;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  onSelectSingleChoice: (questionId: string, optionId: string) => void;
  onUpdateMultipleChoices: (questionId: string, optionIds: readonly string[]) => void;
  onDirectInputSelectedChange: (questionId: string, selected: boolean) => void;
  onUpdateDirectText: (questionId: string, text: string) => void;
  onNavigateNextUnanswered: (questionId: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
};
export const AiBuilderQuestionSetComposer = ({
  questionSet,
  submitting = false,
  onNavigatePrevious,
  onNavigateNext,
  onSelectSingleChoice,
  onUpdateMultipleChoices,
  onDirectInputSelectedChange,
  onUpdateDirectText,
  onNavigateNextUnanswered,
  onSubmit,
  onCancel
}: AiBuilderQuestionSetComposerProps) => {
  const copy = useAiBuilderChatCopy();
  const questionTitleId = useId();
  const question = questionSet.questions.find(candidate => candidate.questionId === questionSet.activeQuestionId);
  if (!question) return null;
  const response = questionSet.responsesByQuestionId[question.questionId];
  const currentAnswer = getActiveQuestionAnswer(questionSet, question.questionId);
  const showsSubmit = canSubmitAiBuilderQuestionSet(questionSet);
  const locked = submitting;
  const selectSingle = (optionId: string) => {
    if (locked) return;
    onSelectSingleChoice(question.questionId, optionId);
  };
  return <section className="flex min-w-0 flex-col gap-xs rounded-md border-2 border-strong bg-default px-xs py-sm shadow-lg" data-sentry-component="AiBuilderQuestionSetComposer" data-sentry-source-file="AiBuilderQuestionSetComposer.tsx">
      <fieldset disabled={locked} aria-labelledby={questionTitleId} className="min-w-0 border-0 p-0">
        <AiBuilderQuestionSetHeader questionTitleId={questionTitleId} activeQuestionId={question.questionId} questions={questionSet.questions} onNavigatePrevious={() => !locked && onNavigatePrevious()} onNavigateNext={() => !locked && onNavigateNext()} onCancel={() => !locked && onCancel()} data-sentry-element="AiBuilderQuestionSetHeader" data-sentry-source-file="AiBuilderQuestionSetComposer.tsx" />

        <div className="mt-xs min-w-0">
          <AiBuilderQuestionAnswerControls question={question} directInputSelected={response?.directInputSelected ?? false} selectedOptionIds={response?.choiceOptionIds ?? []} directText={response?.text ?? ''} locked={locked} onSelectedOptionIdsChange={optionIds => {
          if (question.selectionMode === 'single') {
            const optionId = optionIds[0];
            if (optionId) selectSingle(optionId);
            return;
          }
          onUpdateMultipleChoices(question.questionId, optionIds);
        }} onDirectInputSelectedChange={selected => onDirectInputSelectedChange(question.questionId, selected)} onDirectTextChange={text => onUpdateDirectText(question.questionId, text)} data-sentry-element="AiBuilderQuestionAnswerControls" data-sentry-source-file="AiBuilderQuestionSetComposer.tsx" />
        </div>
      </fieldset>

      <div className="flex min-h-9 shrink-0 justify-end">
        <Button type="button" size="sm" className="shrink-0" data-ai-builder-question-set-cta disabled={locked || !showsSubmit && !currentAnswer} onClick={showsSubmit ? onSubmit : () => onNavigateNextUnanswered(question.questionId)} data-sentry-element="Button" data-sentry-source-file="AiBuilderQuestionSetComposer.tsx">
          {submitting ? <span className="flex items-center justify-center gap-xs" aria-label={copy.questionSet.status.submittingAriaLabel}>
              <LoadingSpinner className="size-md text-current motion-reduce:animate-none" />
              {copy.questionSet.status.submitting}
            </span> : showsSubmit ? copy.questionSet.cta.submit : copy.questionSet.cta.next}
        </Button>
      </div>

      {questionSet.submissionError === 'cancel_failed' ? <div role="alert" className="flex items-center justify-between gap-sm">
          <p className="text-body-sm-medium text-destructive">
            {copy.questionSet.error.cancelFailed}
          </p>
          <Button type="button" variant="outlined" intent="alternative" size="sm" disabled={locked} aria-label={copy.questionSet.error.retryCancelAriaLabel} onClick={onCancel}>
            {copy.questionSet.error.retry}
          </Button>
        </div> : null}
    </section>;
};